import { Box, Chip, Grid, IconButton } from "@material-ui/core";
import { blue, red } from "@material-ui/core/colors";
import { Comment } from "@material-ui/icons";
import IndeterminateCheckBoxIcon from "@material-ui/icons/IndeterminateCheckBox";
import {
    Championship,
    Collections,
    Incident,
    IncidentDefaultValues,
    IncidentOriginDescription,
    IncidentReviewRequest,
    Infraction,
    isIncidentApplied,
    isIncidentNoFurtherAction,
    PenaltyDescriptionByType,
    Protest,
    RaceControl,
    Role,
    StatusIncident,
    StatusIncidentColor,
    StatusIncidentDescription,
    StatusIncidentReviewRequestColor,
    useChampionshipDrivers,
    useCollection,
    useIncidentReviewRequests,
} from "api";
import cloneDeep from "lodash/cloneDeep";
import MaterialTable from "material-table";
import { useSnackbar } from "notistack";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useFirestore, useFirestoreConnect } from "react-redux-firebase";
import { RootState } from "redux/model";
import { BoxCard, LoggedUserContext } from "ui/componentes";
import { CarsNumbersToChip } from "ui/views/ChampionshipForm/RoundEdit/CarsNumbersToChip";
import { IncidentForm } from "ui/views/ChampionshipForm/RoundEdit/IncidentForm";
import { IncidentReviewRequestAnswerDialog } from "ui/views/RaceControlView/IncidentReviewRequestAnswerDialog";
import { LocalizationPTBr } from "util/MaterialTableUtil";
import { ProtestInfoDialog } from "ui/views/IncidentsReportView";
import { IconGavel } from "../../componentes/Icons";

interface IncidentListAfterRaceProps {
    raceControl: RaceControl;
    championship: Championship;
}

interface ProtestInfoState {
    protest?: Protest;
    showDialog: boolean;
}

const confirmIncidentUpdate = (
    firestore,
    incident,
    enqueueSnackbar,
    status: StatusIncident
) => () => {
    firestore
        .collection(Collections.RACES_CONTROL)
        .doc(incident?.raceControlId)
        .collection(Collections.INCIDENTS)
        .doc(incident?.id)
        .update({ status })
        .then(() => {
            enqueueSnackbar("Incidente atualizado!", {
                variant: "success",
                anchorOrigin: {
                    horizontal: "right",
                    vertical: "bottom",
                },
            });
        })
        .catch((error) => {
            console.error(error);
            enqueueSnackbar("Não foi possível atualizar incidente!", {
                variant: "error",
                anchorOrigin: {
                    horizontal: "right",
                    vertical: "bottom",
                },
            });
        });
};

const IncidentListAfterRace: React.FC<IncidentListAfterRaceProps> = ({
    raceControl,
    championship,
}) => {
    const protestsById =
        useCollection<{ [key: string]: Protest }>({
            collection: Collections.PROTESTS,
            where: ["raceControlId", "==", raceControl.id || "-1"],
        }) || {};
    const [protestInfoState, setProtestInfoState] = useState<ProtestInfoState>({
        showDialog: false,
    });
    const loggedUser = useContext(LoggedUserContext);
    const [reviewRequestState, setReviewRequestState] = useState<{
        open: boolean;
        reviewRequest?: IncidentReviewRequest;
    }>({ open: false });

    const [incident, setIncident] = useState<Incident>({
        ...IncidentDefaultValues,
        raceControlId: raceControl.id || "-1",
    });

    useEffect(() => {
        setIncident({
            ...IncidentDefaultValues,
            raceControlId: raceControl.id || "-1",
        });
    }, [raceControl]);

    const { enqueueSnackbar } = useSnackbar();
    const firestore = useFirestore();

    useFirestoreConnect({
        collection: Collections.RACES_CONTROL,
        doc: raceControl.id || "-1",
        subcollections: [{ collection: Collections.INCIDENTS }],
        storeAs: `incidents${raceControl.id || "List"}`,
    });

    const incidents: Incident[] = cloneDeep(
        useSelector((state: RootState) => {
            return state.firestore.ordered[
                `incidents${raceControl.id || "List"}`
            ];
        }) || []
    );

    incidents.sort((i1, i2) => {
        if (i1.lap < i2.lap) return -1;
        if (i1.lap > i2.lap) return 1;
        return 0;
    });

    const drivers = useChampionshipDrivers(championship?.id);

    const driversByNumber = useMemo(() => {
        if (championship) {
            const driversByNumber = {};
            if (drivers && drivers.length > 0) {
                drivers.forEach((driver) => {
                    driversByNumber[
                        driver.usedNumber
                    ] = `#${driver.usedNumber} - ${driver.displayName}`;
                });
            }
            return driversByNumber;
        }
        return {};
    }, [championship, drivers]);

    const infractions =
        useCollection<object>({
            collection: Collections.INFRACTIONS,
        }) || {};

    const incidentReviewRequests = useIncidentReviewRequests({
        userId: loggedUser.roles.includes(Role.ADMIN) ? "" : loggedUser.id,
        roundId: raceControl.roundId || "",
    });

    const incidentReviewRequestsByIncidentId = useMemo(() => {
        const incidentReviewRequestsByIncidentId = {};
        incidentReviewRequests.forEach(
            (review) =>
                (incidentReviewRequestsByIncidentId[review.incidentId] = review)
        );
        return incidentReviewRequestsByIncidentId;
    }, [incidentReviewRequests]);

    return (
        <Grid container spacing={2}>
            <IncidentReviewRequestAnswerDialog
                open={reviewRequestState.open}
                reviewRequest={reviewRequestState.reviewRequest}
                onClose={() => setReviewRequestState({ open: false })}
            />
            <ProtestInfoDialog
                show={protestInfoState.showDialog}
                protest={protestInfoState.protest}
                driversByNumber={driversByNumber}
                canUpdateRaceControlAnswer
                onClose={() =>
                    setProtestInfoState({
                        showDialog: false,
                        protest: undefined,
                    })
                }
            />
            <Grid item xs={12}>
                <IncidentForm
                    championship={championship}
                    incident={incident}
                    onCancel={() => {
                        setIncident({
                            ...IncidentDefaultValues,
                            raceControlId: raceControl.id || "-1",
                        });
                    }}
                    roundId={raceControl.roundId || ""}
                    drivers={drivers}
                />
            </Grid>
            <Grid item xs={12}>
                <MaterialTable
                    title="Incidentes"
                    components={{ Container: BoxCard }}
                    columns={[
                        {
                            field: "origin",
                            title: "Origem",
                            width: "7%",
                            align: "center",
                            render: (incident) => {
                                const protest =
                                    protestsById[incident.protestId || ""];
                                return (
                                    <Box
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="center"
                                    >
                                        {
                                            IncidentOriginDescription[
                                                incident.origin
                                            ]
                                        }
                                        {protest && (
                                            <IconButton
                                                style={{ padding: 4 }}
                                                onClick={() =>
                                                    setProtestInfoState({
                                                        protest: {
                                                            ...protest,
                                                            id:
                                                                incident.protestId ||
                                                                "",
                                                        },
                                                        showDialog: true,
                                                    })
                                                }
                                                title="Ver protesto"
                                            >
                                                <IconGavel size="sm" />
                                            </IconButton>
                                        )}
                                    </Box>
                                );
                            },
                        },
                        {
                            field: "lap",
                            title: "Volta",
                            align: "center",
                            editable: "never",
                        },
                        {
                            field: "notifiedBy",
                            title: "Notificado por",
                            editable: "never",
                            render: (incident) => {
                                if (incident.notifiedBy) {
                                    return (
                                        <CarsNumbersToChip
                                            carNumbers={incident.notifiedBy}
                                            driversByNumber={driversByNumber}
                                        />
                                    );
                                }
                                return "";
                            },
                        },
                        {
                            field: "involvedCars",
                            title: "Carros envolvidos",
                            render: (incident) => {
                                if (incident.involvedCars) {
                                    return (
                                        <CarsNumbersToChip
                                            carNumbers={incident.involvedCars}
                                            driversByNumber={driversByNumber}
                                            color="primary"
                                        />
                                    );
                                }
                                return "";
                            },
                            editable: "never",
                        },
                        {
                            field: "guiltyCar",
                            title: "Culpado",
                            render: (incident) => {
                                if (incident.guiltyCar) {
                                    return (
                                        <CarsNumbersToChip
                                            carNumbers={
                                                incident.guiltyCar === "-1"
                                                    ? []
                                                    : [incident.guiltyCar]
                                            }
                                            driversByNumber={driversByNumber}
                                            style={{
                                                backgroundColor: red[500],
                                                color: "white",
                                            }}
                                        />
                                    );
                                }
                                return "";
                            },
                            editable: "never",
                        },
                        {
                            field: "infractionId",
                            title: "Infração / Penalidade",
                            render: (incident) => {
                                if (incident.infractionId) {
                                    const infraction: Infraction =
                                        infractions[incident.infractionId];
                                    if (!infraction) {
                                        return "";
                                    }
                                    return `${infraction.key} / ${
                                        PenaltyDescriptionByType[
                                            infraction.penaltyType || ""
                                        ]
                                    }`;
                                }
                                return "";
                            },
                            editable: "never",
                        },
                        {
                            field: "status",
                            title: "Status",
                            align: "center",
                            editable: "never",
                            render: (incident) => (
                                <Chip
                                    label={
                                        StatusIncidentDescription[
                                            incident.status
                                        ]
                                    }
                                    style={StatusIncidentColor[incident.status]}
                                    size="small"
                                />
                            ),
                        },
                    ]}
                    data={incidents}
                    actions={[
                        (incident) => ({
                            icon: "check",
                            tooltip: "Marcar como aplicado",
                            disabled:
                                !incident.infractionId ||
                                isIncidentApplied(incident) ||
                                !incident.guiltyCar,
                            onClick: () => {
                                if (incident.infractionId) {
                                    confirmIncidentUpdate(
                                        firestore,
                                        incident,
                                        enqueueSnackbar,
                                        StatusIncident.APPLIED_AFTER_RACE
                                    )();
                                }
                            },
                        }),
                        (incident) => ({
                            icon: () => <IndeterminateCheckBoxIcon />,
                            tooltip: "Nenhuma ação necessária",
                            disabled:
                                isIncidentNoFurtherAction(incident) ||
                                !!incident.guiltyCar,
                            onClick: () => {
                                confirmIncidentUpdate(
                                    firestore,
                                    incident,
                                    enqueueSnackbar,
                                    StatusIncident.NO_FURTHER_ACTION
                                )();
                            },
                        }),
                        (incident) => ({
                            icon: "edit",
                            tooltip: "Editar",
                            onClick: () => {
                                setIncident(incident);
                            },
                        }),
                        (incident) => {
                            const incidentReviewRequest =
                                incidentReviewRequestsByIncidentId[
                                    incident.id || ""
                                ];

                            return {
                                icon: () => (
                                    <Comment
                                        style={{
                                            color:
                                                StatusIncidentReviewRequestColor[
                                                    incidentReviewRequest?.status ||
                                                        ""
                                                ],
                                        }}
                                    />
                                ),
                                tooltip: "Ver pedido de revisão",
                                hidden: !incidentReviewRequest,
                                onClick: () => {
                                    setReviewRequestState({
                                        open: true,
                                        reviewRequest: incidentReviewRequest,
                                    });
                                },
                            };
                        },
                    ]}
                    localization={LocalizationPTBr}
                    editable={{
                        onRowDelete: (incident) => {
                            return firestore
                                .collection(Collections.RACES_CONTROL)
                                .doc(raceControl.id)
                                .collection(Collections.INCIDENTS)
                                .doc(incident.id)
                                .delete()
                                .then(() => {
                                    enqueueSnackbar("Incident removido!", {
                                        variant: "success",
                                        anchorOrigin: {
                                            horizontal: "right",
                                            vertical: "bottom",
                                        },
                                    });
                                })
                                .catch((error) => {
                                    console.error(error);
                                    enqueueSnackbar(
                                        "Não foi possível remover o incident!",
                                        {
                                            variant: "error",
                                            anchorOrigin: {
                                                horizontal: "right",
                                                vertical: "bottom",
                                            },
                                        }
                                    );
                                });
                        },
                    }}
                    options={{
                        draggable: false,
                        sorting: false,
                        search: false,
                        paging: false,
                        rowStyle: (data) => {
                            const rowIncident = data as Incident;
                            if (rowIncident.id === incident.id) {
                                return {
                                    backgroundColor: red["100"],
                                };
                            }
                            if (rowIncident.protestId) {
                                return {
                                    fontSize: "0.857rem",
                                    backgroundColor: blue["50"],
                                };
                            }
                            return {};
                        },
                    }}
                />
            </Grid>
        </Grid>
    );
};

export { IncidentListAfterRace };
